import { Comment } from "../components/comment/Comment";
import { CommentBody } from "../components/comment/CommentBody";
import { ApplicationEvaluationResult, OnboardingCommentDocument } from "../comment/comment.model";


interface OnboardingCommentProps {
    comment: OnboardingCommentDocument;
}

export const OnboardingComment = (props: OnboardingCommentProps) => {
    const { comment } = props;

    const gardener = comment.gardenerName;

    const title = (() => {
        switch ( comment.data.event ) {
            case "application checked": return "Ansøger udtaget til samtale";
            case "called applicant": return "Vi fik ikke fat i ansøger ift. onboarding";
            case "application approved": return "Ansøgning godkendt";
            case "application auto-approved": return "Ansøgning godkendt af AI";
            case "application auto-rejected": return "Ansøgning afvist af AI";
            case "profile activated": return "Profil aktiveret";
        }
    })();

    const message = (() => {
        switch ( comment.data.event ) {
            case "application checked":
                return (
                    <>
                        Ansøgning fra {gardener} blev læst igennem og ansøgeren blev udtaget til samtale.
                    </>
                );
            case "called applicant":
                return (
                    <>
                        Vi har ringet til ansøgeren {gardener} {comment.data.count === 1 ? "én gang" : "to gange"}, men fik ikke fat i dem. Vi vil forsøge {comment.data.count === 1 ? "to gange" : "én gang"} til.
                    </>
                );
            case "application approved":
                return (
                    <>
                        Ansøgning fra {gardener} blev godkendt og ansøgeren kan nu aktivere sin profil.
                    </>
                );
            case "application auto-approved":
                return <EvaluationSummary gardenerName={gardener} verdict="approved" evaluation={comment.data.evaluation} />;
            case "application auto-rejected":
                return <EvaluationSummary gardenerName={gardener} verdict="rejected" evaluation={comment.data.evaluation} />;
            case "profile activated":
                return (
                    <>
                        Havemanden har aktiveret sin profil og kan nu modtage opgaver.
                    </>
                );
        }
    })();

    return (
        <Comment date={comment.creationDate.toDate().toISOString()} title={title} author={comment.administratorName ?? undefined}>
            <CommentBody>
                {message}
            </CommentBody>
        </Comment>
    );
};

const criterionLabel: Record<keyof Pick<ApplicationEvaluationResult, "eligible" | "serious" | "danish" | "experience" | "nameValid" | "companyMatch">, string> = {
    eligible: "Egnet",
    serious: "Seriøs",
    danish: "Dansk",
    experience: "Erfaring",
    nameValid: "Navn gyldigt",
    companyMatch: "Virksomhed matcher",
};

const EvaluationSummary = ({ gardenerName, verdict, evaluation }: { gardenerName: string; verdict: "approved" | "rejected"; evaluation: ApplicationEvaluationResult }) => {
    const criteria: Array<{ key: keyof typeof criterionLabel; value: boolean | null; comment: string | null }> = [
        { key: "eligible", value: evaluation.eligible, comment: evaluation.eligibleComment },
        { key: "serious", value: evaluation.serious, comment: evaluation.seriousComment },
        { key: "danish", value: evaluation.danish, comment: evaluation.danishComment },
        { key: "experience", value: evaluation.experience, comment: evaluation.experienceComment },
        { key: "nameValid", value: evaluation.nameValid, comment: evaluation.nameValidComment },
        { key: "companyMatch", value: evaluation.companyMatch, comment: evaluation.companyMatchComment },
    ];

    const headline = verdict === "approved"
        ? `Ansøgning fra ${gardenerName} blev godkendt af AI og ansøgeren kan nu aktivere sin profil.`
        : `Ansøgning fra ${gardenerName} blev afvist af AI og er derfor sendt til manuel gennemgang.`;

    return (
        <>
            {headline}
            <table style={{ marginTop: 8, borderCollapse: "collapse", width: "100%" }}>
                <tbody>
                    {criteria.map(({ key, value, comment }) => value !== null && (
                        <tr key={key}>
                            <td style={{ paddingRight: 12, fontWeight: 600, whiteSpace: "nowrap", verticalAlign: "top" }}>
                                {value ? "✓" : "✗"} {criterionLabel[key]}
                            </td>
                            <td style={{ color: "#555", verticalAlign: "top" }}>
                                {comment || "—"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
};
